import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Activity, ChevronRight, LockKeyhole, Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/SupabaseAuthContext';

const googleEnabled = process.env.REACT_APP_ENABLE_GOOGLE_AUTH === 'true';

export const SupabaseLoginPage = () => {
  const { authError, resetPassword, signIn, signInWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(location.state?.authError || authError || '');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authError) setError(authError);
  }, [authError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!email.trim() || !password) {
      setError('Complete all required fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      await signIn({ email, password });
      navigate('/dashboard', { replace: true });
    } catch (submissionError) {
      setError(submissionError.message || 'Unable to authenticate.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogle = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signInWithGoogle();
    } catch (oauthError) {
      setError(oauthError.message || 'Unable to start Google sign in.');
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setNotice('');
    if (!email.trim()) {
      setError('Enter your email above, then tap “Forgot password?”.');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(email);
      setNotice('If an account exists for that email, a password reset link is on its way.');
    } catch (resetError) {
      setError(resetError.message || 'Unable to send the reset email.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1758637612187-3f495543804b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')",
        }}
      >
        <div className="absolute inset-0 bg-[#196a70]/85" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <img src="/pranagen-logo.png" alt="PranaGen Bio" className="h-12 w-auto object-contain brightness-0 invert" />
          <div className="space-y-6">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight">
              Advanced Biological<br />Age Assessment
            </h1>
            <p className="text-lg text-white/80 max-w-md leading-relaxed">
              Secure clinician workspaces for validated biological age assessments and longitudinal patient care.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Tenant-isolated data</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full">
                <Activity className="w-4 h-4" />
                <span className="text-sm">Validated clocks</span>
              </div>
            </div>
          </div>
          <p className="text-white/60 text-sm">© 2026 PranaGen Bio</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-7">
          <div className="lg:hidden flex justify-center">
            <img src="/pranagen-logo.png" alt="PranaGen Bio" className="h-12" />
          </div>
          <div className="text-center lg:text-left">
            <h2 className="font-heading text-3xl font-bold text-slate-900">
              Welcome back
            </h2>
            <p className="mt-2 text-slate-600">
              Sign in to your secure clinical workspace.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="pl-10"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="clinician@example.com"
                  value={email}
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={submitting}
                  className="text-xs font-medium text-[#196a70] hover:underline disabled:opacity-50"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="pl-10"
                  minLength={8}
                  onChange={(event) => setPassword(event.target.value)}
                  value={password}
                />
              </div>
            </div>
            {error && <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>}
            {notice && <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">{notice}</div>}
            <Button
              type="submit"
              disabled={submitting || Boolean(authError)}
              className="w-full bg-[#196a70] hover:bg-[#12565b] text-white py-6 text-base rounded-xl"
            >
              {submitting ? 'Please wait...' : 'Sign in'}
              {!submitting && <ChevronRight className="w-5 h-5 ml-auto" />}
            </Button>
          </form>

          {googleEnabled && (
            <Button type="button" variant="outline" onClick={handleGoogle} disabled={submitting} className="w-full py-6">
              Continue with Google
            </Button>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/demo')}
            className="w-full py-6"
            data-testid="view-demo-btn"
          >
            View live demo
          </Button>
        </div>
      </div>
    </div>
  );
};
